import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';

// --- NUEVA CONSTANTE ---
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB

const FileUpload = ({
  onFileSelect,
  accept = 'image/*,video/*',
  maxSize = MAX_FILE_SIZE, // --- VALOR POR DEFECTO ACTUALIZADO ---
  label,
  multiple = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [singleFilePreview, setSingleFilePreview] = useState(null);
  const fileInputRef = useRef(null);

  const finalLabel = label || t('file_upload.default_label');

  const validateFile = (file) => {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    if (file.size > maxSize) {
      return t('file_upload.error_size', {
        fileName: file.name,
        maxSize: maxSizeMB,
      });
    }

    // --- LÓGICA DE VALIDACIÓN MEJORADA ---
    const acceptedTypes = accept.split(',').map((t) => t.trim());
    const fileType = file.type;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    const isAccepted = acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        // Comprobar por extensión (ej: .mkv)
        return fileExtension === type.substring(1);
      }
      if (type.endsWith('/*')) {
        // Comprobar por tipo principal (ej: image/*)
        return fileType.startsWith(type.split('/')[0]);
      }
      // Comprobar por tipo exacto (ej: video/mp4)
      return type === fileType;
    });

    if (!isAccepted) {
      return t('file_upload.error_type', {
        fileName: file.name,
        acceptedTypes: accept,
      });
    }
    return null;
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      onFileSelect(multiple ? [] : null);
      setSingleFilePreview(null);
      setError(null);
      return;
    }

    setError(null);
    let currentErrors = [];
    const validFiles = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileError = validateFile(file);
      if (fileError) {
        currentErrors.push(fileError);
      } else {
        validFiles.push(file);
      }
    }

    if (currentErrors.length > 0) {
      setError(currentErrors.join('\n'));
      onFileSelect(multiple ? [] : null);
      setSingleFilePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (!multiple && validFiles.length > 0) {
      const firstFile = validFiles[0];
      if (firstFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => setSingleFilePreview(reader.result);
        reader.readAsDataURL(firstFile);
      } else if (firstFile.type.startsWith('video/')) {
        setSingleFilePreview(URL.createObjectURL(firstFile));
      } else {
        setSingleFilePreview(null);
      }
    } else {
      setSingleFilePreview(null);
    }
    onFileSelect(multiple ? validFiles : validFiles[0] || null);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setError(null);
    setSingleFilePreview(null);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const mockEvent = { target: { files } };
      handleFileChange(mockEvent);
    }
  };

  const resetSelection = () => {
    setSingleFilePreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(multiple ? [] : null);
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-neutral-50 dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl min-h-[150px] py-6 px-3 transition 
          ${
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:border-primary dark:hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10'
          }`}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {!multiple && singleFilePreview ? (
          <div className="flex flex-col items-center gap-2">
            {singleFilePreview.startsWith('data:image') ? (
              <img
                src={singleFilePreview}
                alt={t('file_upload.preview_alt')}
                className="object-contain rounded-lg max-h-40 shadow-sm"
              />
            ) : (
              <video
                src={singleFilePreview}
                controls
                className="object-contain rounded-lg max-h-40 shadow-sm"
              />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                resetSelection();
              }}
              className="mt-2 text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full hover:bg-red-200"
            >
              {t('file_upload.remove_button')}
            </button>
          </div>
        ) : (
          <>
            <FontAwesomeIcon
              icon={faCloudUploadAlt}
              className="text-4xl text-primary mb-1"
            />
            <div className="font-semibold text-neutral-700 dark:text-neutral-300 text-center">
              {finalLabel}
            </div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
              {t('file_upload.drop_text')}
            </div>
            {multiple && (
              <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 text-center">
                {t('file_upload.multiple_text')}
              </div>
            )}
          </>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={accept} // --- USAR LA PROP ACCEPT ---
          multiple={multiple}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 text-center bg-red-50 dark:bg-red-900/30 rounded-lg px-3 py-2 mt-1 whitespace-pre-line">
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
